import * as React from 'react';
import {
  StyledButton, CenterModal, Alert, SearchBar, Searchbox,
} from 'components';
import COLOR from 'constants/color';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import BROADCAST_PREFERENCES from 'constants/broadcastPreference';
import Checkbox from 'components/input/checkbox';
import { Organization } from 'models/organization';
import { faSearch } from '@fortawesome/free-solid-svg-icons';

interface Props {
  form: {
    is_qa: boolean;
    broadcast_preference?: string;
  };
  disabled?: boolean;
  disableFullEdit?: boolean;
  orgId: string;
  hasNoSquad?: boolean;
  organizations: Organization[];
  onFormChange: (fieldName: string, value: string | boolean) => void;
  vesselForm?: React.ReactNode;
}

const GeneralInfoForm = (props: Props): JSX.Element => {
  const broadcastPreference = props.form && props.form.broadcast_preference;
  const orgId = props.orgId || '';
  const disableFullEdit = props.disableFullEdit || false;
  const editLocked = props.disabled || false;
  const [showNoSquadModal, setNoSquadModal] = React.useState(false);
  const [org, setOrg] = React.useState(props.organizations[0]);
  const history = useHistory();

  React.useEffect(() => {
    const orgDetail = props.organizations.find((org) => org.id === orgId);
    setOrg(orgDetail);
  }, [props.organizations]);

  return (
    <Card>
      <Title>
        Organisation ID <RedText>*</RedText>
      </Title>
      <SearchContainer>
        {(disableFullEdit && (
          <Searchbox
            trailingIcon={faSearch}
            isRequired
            placeholder="Wonderland Pte Ltd"
            width="large"
            disabled
            value={org && org.business_name}
            onTextChange={(): void => {
              return;
            }}
          />
        )) || (
          <SearchBar
            label=""
            value={org && org.business_name}
            onChange={(v: string): void => {
              props.onFormChange('org_id', v || '');
            }}
            onTextChange={(v): void => {
              if (v === '') {
                props.onFormChange('org_id', v || '');
              }
            }}
            options={props.organizations.map((org) => {
              return {
                value: org.id,
                text: org.business_name,
              };
            })}
            placeholder="Wonderland Pte Ltd"
            required
          />
        )}
      </SearchContainer>
      <IsQACheckbox>
        <Checkbox
          onClick={(): void => {
            props.onFormChange('is_qa', !props.form.is_qa);
          }}
          selected={props.form.is_qa}
          disabled={editLocked}
        >
          Is QA?
        </Checkbox>
      </IsQACheckbox>
      <BroadcastPreferencesContainer>
        <Title>
          Select a Service <RedText>*</RedText>
        </Title>
        <BroadcastPreferencesButtonContainer>
          {!disableFullEdit && props.hasNoSquad ? (
            <ClickableDisabledButton
              onClick={(): void => {
                props.orgId && setNoSquadModal(true);
              }}
            >
              Squad
            </ClickableDisabledButton>
          ) : (
            <StyledButton
              buttonStyle="encourage"
              buttonType="neutral"
              onClick={(): void => {
                props.onFormChange('broadcast_preference', BROADCAST_PREFERENCES.squad);
              }}
              disabled={
                (!disableFullEdit && props.hasNoSquad) ||
                (disableFullEdit && broadcastPreference !== BROADCAST_PREFERENCES.squad)
              }
            >
              Squad
            </StyledButton>
          )}
          <StyledButton
            buttonStyle="encourage"
            buttonType="neutral"
            disabled={
              disableFullEdit && broadcastPreference !== BROADCAST_PREFERENCES.all
            }
            onClick={(): void => {
              props.onFormChange('broadcast_preference', BROADCAST_PREFERENCES.all);
            }}
          >
            Standard
          </StyledButton>
          <StyledButton
            buttonStyle="encourage"
            buttonType="neutral"
            disabled={
              disableFullEdit && broadcastPreference !== BROADCAST_PREFERENCES.marine
            }
            onClick={(): void => {
              props.onFormChange('broadcast_preference', BROADCAST_PREFERENCES.marine);
            }}
          >
            Marine
          </StyledButton>
        </BroadcastPreferencesButtonContainer>
        <BroadcastPreferencesInfo>
          <Alert status="info">
            {broadcastPreference === BROADCAST_PREFERENCES.squad && (
              <>Broadcast your Orders to your Squad</>
            )}
            {broadcastPreference === BROADCAST_PREFERENCES.all && (
              <>Broadcast your Orders to all GotSurge Driver-Partners</>
            )}
            {broadcastPreference === BROADCAST_PREFERENCES.marine && (
              <>Book for a Transporter to and from Marine ports</>
            )}
          </Alert>
        </BroadcastPreferencesInfo>
      </BroadcastPreferencesContainer>
      {props.vesselForm ? <VesselFormContainer>
        {props.vesselForm}
      </VesselFormContainer> : false}
      {showNoSquadModal && (
        <CenterModal
          leftButtonOnClick={(): void => {
            setNoSquadModal(false);
          }}
          leftButtonText="Back"
          rightButtonStyle="encourage"
          rightButtonType="secondary"
          rightButtonOnClick={(): void => {
            history.push(`/sys/organizations/${props.orgId}/squad`);
          }}
          rightButtonText="Invite member"
          title="You have no squad member yet"
        >
          Add your squad member or select another service.
        </CenterModal>
      )}
    </Card>
  );
};

const IsQACheckbox = styled.div`
  width: 5rem;
`;

const Card = styled.div`
  border-radius: 0.5rem;
  border: 1px solid ${COLOR.neutral};
  background-color: ${COLOR.white};
  padding: 1.875rem 2.125rem;
  margin-bottom: 1.75rem;
`;

const Title = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  margin-right: auto;
  color: ${COLOR.darkGray};
`;

const RedText = styled.span`
  color: ${COLOR.red};
`;

const BroadcastPreferencesContainer = styled.section`
  margin-top: 1rem;
`;

const VesselFormContainer = styled.section`
  margin-top: 1rem;
`;

const BroadcastPreferencesButtonContainer = styled.div`
  display: flex;
  margin-top: 1rem;

  & > *:not(:last-child) {
    margin-right: 0.5rem;
  }
`;

const ClickableDisabledButton = styled.div`
  background-color: ${COLOR.neutral};
  border-color: ${COLOR.grey};
  border-radius: 0.5em;
  border-width: 0;
  color: ${COLOR.grey};
  cursor: not-allowed;
  font-size: 1rem;
  line-height: 1.125em;
  padding: 0.5rem 0.75rem;
  width: auto;
`;

const BroadcastPreferencesInfo = styled.div`
  margin-top: 1rem;
  width: 25rem;
  font-size: 0.75rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchContainer = styled.div`
  margin: 0.5rem 0;
`;

export default GeneralInfoForm;
